import {Button, Image, message, Tag} from 'antd'
import {course_submitIdentityDocuments_Api} from 'apis'
import {callApi} from 'modules/asyncCache/callApi'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useRef, useState} from 'react'
import {BiCloudUpload} from 'react-icons/bi'
import {MdOutlineBlock} from 'react-icons/md'
import {RiShieldCheckFill, RiShieldCheckLine} from 'react-icons/ri'
import {formatDate} from '../../../../../helpers/datetimeHelpers'

export const IDENTITY_STATUS = {
  UNVERIFIED: 0,
  PENDING: -1,
  VERIFIED: 1,
  REJECTED: -2,
  BLOCKED: -3,
}

export const IDENTITY_STATUS_CONFIG = {
  [IDENTITY_STATUS.UNVERIFIED]: {label: 'identity unverified', color: 'default'},
  [IDENTITY_STATUS.PENDING]: {label: 'identity pending', color: 'orange'},
  [IDENTITY_STATUS.VERIFIED]: {label: 'identity verified', color: 'green'},
  [IDENTITY_STATUS.REJECTED]: {label: 'identity rejected', color: 'red'},
  [IDENTITY_STATUS.BLOCKED]: {label: 'identity blocked', color: 'purple'},
}

export const FileUploadArea = ({label, file, existingUrl, onChange}) => {
  const t = useTranslate()
  const inputRef = useRef(null)
  const previewUrl = file ? URL.createObjectURL(file) : existingUrl

  return (
    <div className="flex flex-col items-center gap-1.5">
      <span className="text-xs text-color-400">{label}</span>
      <button
        type="button"
        className="relative rounded-lg border-2 border-dashed border-color-200 hover:border-indigo-400 transition-colors overflow-hidden"
        style={{width: 120, height: 80}}
        onClick={() => inputRef.current && inputRef.current.click()}>
        {previewUrl ? (
          <>
            <img
              src={previewUrl}
              className="w-full h-full object-cover"
              alt={label}
            />
            <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity bg-black bg-opacity-30 rounded">
              <BiCloudUpload size={20} className="text-white"/>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center h-full gap-1 px-2">
            <BiCloudUpload size={22} className="text-color-300"/>
            <span className="text-2xs text-color-400 text-center leading-tight">{t('click to upload')}</span>
          </div>
        )}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files && e.target.files[0]
          if (f) onChange(f)
          e.target.value = ''
        }}
      />
    </div>
  )
}

export const IdentityVerificationSection = ({item, onRefresh}) => {
  const t = useTranslate()
  const [frontFile, setFrontFile] = useState(null)
  const [backFile, setBackFile] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  const status = item.identity_status != null ? item.identity_status : IDENTITY_STATUS.UNVERIFIED
  const cfg = IDENTITY_STATUS_CONFIG[status] ?? IDENTITY_STATUS_CONFIG[IDENTITY_STATUS.UNVERIFIED]

  const isVerified = status === IDENTITY_STATUS.VERIFIED
  const isPending = status === IDENTITY_STATUS.PENDING
  const isBlocked = status === IDENTITY_STATUS.BLOCKED
  const isRejected = status === IDENTITY_STATUS.REJECTED
  const canUpload = item.edit === true && !isVerified && !isBlocked

  const handleSubmit = useCallback(() => {
    const values = {}
    if (frontFile) values.front = frontFile
    if (backFile) values.back = backFile
    if (!values.front && !values.back) {
      message.warning(t('please upload at least one document'))
      return
    }
    setSubmitting(true)
    callApi({
      apiInfo: course_submitIdentityDocuments_Api,
      query: {id: item.id},
      values,
    }).then(() => {
      message.success(t('documents submitted successfully'))
      setFrontFile(null)
      setBackFile(null)
      setSubmitting(false)
      onRefresh && onRefresh()
    }).catch(() => {
      message.error(t('upload failed'))
      setSubmitting(false)
    })
  }, [frontFile, backFile, item.id, onRefresh, t])

  const hasDocs = item.identity_document_front_url || item.identity_document_back_url

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2 pb-1.5 border-b border-color-50">
        <RiShieldCheckLine
          size={13}
          className={isVerified ? 'text-green-500' : 'text-color-400'}
        />
        <span className="text-xs font-semibold uppercase tracking-widest text-color-400">
          {t('identity verification')}
        </span>
      </div>

      {/* Status badge */}
      <div className="flex items-center gap-2 flex-wrap">
        <Tag color={cfg.color} className="flex items-center gap-1">
          {isVerified && <RiShieldCheckFill size={11} className="inline"/>}
          {isBlocked && <MdOutlineBlock size={11} className="inline"/>}
          {t(cfg.label)}
        </Tag>
        {isVerified && item.identity_verified_at && (
          <span className="text-xs text-color-400 italic">
            {formatDate(item.identity_verified_at, 'DD/MM/YYYY HH:mm')}
          </span>
        )}
      </div>

      {/* Rejection / blocked reason */}
      {(isRejected || isBlocked) && item.identity_rejected_reason && (
        <div className="flex gap-2 p-2.5 rounded-lg bg-red-50 border border-red-100 text-sm text-red-600 italic">
          {item.identity_rejected_reason}
        </div>
      )}

      {/* Pending message */}
      {isPending && (
        <div className="text-xs text-orange-500 italic">
          {t('identity pending review')}
        </div>
      )}

      {/* Existing uploaded images */}
      {hasDocs && (
        <div className="flex gap-3 flex-wrap">
          {item.identity_document_front_url && (
            <div className="flex flex-col items-center gap-1">
              <span className="text-xs text-color-400">{t('identity document front')}</span>
              <Image
                src={item.identity_document_front_url}
                width={100}
                height={68}
                className="rounded-lg object-cover border border-color-100"
                alt={t('identity document front')}
              />
            </div>
          )}
          {item.identity_document_back_url && (
            <div className="flex flex-col items-center gap-1">
              <span className="text-xs text-color-400">{t('identity document back')}</span>
              <Image
                src={item.identity_document_back_url}
                width={100}
                height={68}
                className="rounded-lg object-cover border border-color-100"
                alt={t('identity document back')}
              />
            </div>
          )}
        </div>
      )}

      {/* Upload form */}
      {canUpload && (
        <div className="flex flex-col gap-3 p-3 background-50 rounded-xl border border-color-50">
          <span className="text-xs text-color-400 italic">
            {isRejected ? t('re-upload identity documents') : t('upload identity documents')}
          </span>
          <div className="flex gap-4 flex-wrap">
            <FileUploadArea
              label={t('identity document front')}
              file={frontFile}
              existingUrl={null}
              onChange={setFrontFile}
            />
            <FileUploadArea
              label={t('identity document back')}
              file={backFile}
              existingUrl={null}
              onChange={setBackFile}
            />
          </div>
          <div className="flex justify-end">
            <Button
              type="primary"
              size="small"
              loading={submitting}
              disabled={!frontFile && !backFile}
              onClick={handleSubmit}>
              {t('submit documents')}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
